import { MobileHelper } from "./../../../shared/helpers/mobile-helper";
import { AppEvents } from "./../../../app-events";
import { DocumentEvents } from "./../../helpers/document-events";
import { CaseRelationModel } from "./../../models/case-relation";
import { BaseRelationModel } from "./../../models/base-relation";
import { GenericTabView } from "./generic-tab";
import { SortableCollection, SortDictionary } from "./../../collections/sortable-collection";
import { ISorter } from "./../../interfaces/sorter";
import { GenericTabModel } from "./../../models/tabs/generic-tab";
import { ListControlsView } from "./list-controls";
import { GenericListView } from "./generic-list";
import { transient, Container } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../../decorators/backbone-properties";
import { ListTabModel } from "../../models/tabs/list-tab";
import { ContainerHelper } from "../../../shared/helpers/container-helper";

const template = require<string>("./generic-list-tab.html");

const dom = {
    SHOW_ALL_RELATIONS_LINK: ".js-jurisdiction-filter",
    RELATIONS_INFO: ".relations-jurisdiciton-label",
    RELATIONS_BAR: ".relations-jurisdiction-info",
    COLLAPSE_HEADING: ".collapse-heading",
    MOBILE_REQUEST_ALL_RELATIONS: ".mobile-request-all-relations",
    MOBILE_HIDDEN_NEUTRAL_TREATMENTS_ALERT: ".mobile-hidden-neutral-treatments-alert"

};

@transient()
@backboneViewProperties({
    tagName: "div",
    template: template
})
export class GenericListTabView extends GenericTabView {

    public model: ListTabModel;

    protected controlsRegion: Marionette.Region;
    protected listRegion: Marionette.Region;
    protected controlsView: Marionette.View<any>;
    protected listView: GenericListView;

    protected appEvents: Backbone.Events;

    public constructor(private options?) {
        super(options);
        this.appEvents = ContainerHelper.Events;
        this.setupModelListeners();
    }
    public setupModelListeners(): void {
        this.listenTo(this.model.State, "change:ShowAllRelations", this.showAllRelationsChanged);
        this.listenTo(this.model.RelationCollection, "add", this.relationCollectionChanged);
        this.listenTo(this.model.RelationCollection, "reset", this.relationCollectionChanged);
    }

    public regions(): any {
        return {
            controlsRegion: ".controls-region",
            listRegion: ".list-region"
        };
    }

    public relationCollectionChanged() {
        this.setJurisdictionResultsMessage();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events[`click ${dom.SHOW_ALL_RELATIONS_LINK}`] = (e) => this.showAllJurisdictionsClicked(e);
        events["click " + dom.COLLAPSE_HEADING] = (e) => this.collapseHeadingClick(e);

        const mh: MobileHelper = Container.instance.get(MobileHelper); // Can't use the class reference to this, guess that code hasn't run to initialise it at this point
        if (mh.IsMobileMode) {
            events["click " + dom.MOBILE_REQUEST_ALL_RELATIONS] = (e) => this.mobileRequestAllRelationsClick(e);
        }
        return events;
    }

    public onShow() {
        this.setupSubViews();
        this.setJurisdictionResultsMessage();
        this.addMessageToEmptyTabs();
    }

    protected addMessageToEmptyTabs() {
        if (!this.model.RelationCollection || this.model.RelationCollection.length === 0) {
            this.$(".no-content").html(this.model.EmptyCollectionText);
            this.$(".no-content").show();
        } else {
            this.$(".no-content").hide();
        }
    }

    protected setJurisdictionResultsMessage() {
        let info: string;
        let myjurisdictionRelationsCount: number;
        let totalRelationsCount: number;

        const $link = this.$(dom.SHOW_ALL_RELATIONS_LINK);
        const $bar = this.$(dom.RELATIONS_BAR);
        const $label = this.$(dom.RELATIONS_INFO);

        if (!$bar.length) {
            return;
        }

        if (!this.model.RelationCollection || this.model.RelationCollection.length === 0 ) {
            $bar.hide();
            return;
        }

        const areWeFiltering = !this.model.State.ShowAllRelations;

        if (areWeFiltering && this.model.RelationCollection) {
            myjurisdictionRelationsCount = this.model.RelationCollection.where( (m: BaseRelationModel) => m.IsHiddenJurisdiction === false && m.IsActuallyVisible === true ).length;
            totalRelationsCount = this.model.RelationCollection.length;
            // if results count doesn"t exclude any relations just return
            if (myjurisdictionRelationsCount === totalRelationsCount) {
                $bar.hide();
                this.$(".analysis-section-content").removeClass("with-filter"); // take off the with-filter class if we aren't actually excluding any
                return;
            }
            const mh: MobileHelper = Container.instance.get(MobileHelper);
            if (mh.IsMobileMode) {// show simplified message for mobile. Relevant because otherwise numbers are wrong when neutrals haven't loaded
                info = "Some cases may be hidden due to your jurisdiction settings";

            } else {
                info = "Showing " + myjurisdictionRelationsCount + " of " + totalRelationsCount + " relationship";
                if (totalRelationsCount > 1) {
                    info += "s";
                }
                info += " based on your jurisdictions";
            }
        } else {
            info = "Showing all relationships";
        }
        $bar.show();
        $label.html(info);
        this.$(".analysis-section-content").addClass("with-filter");

        $link.text(areWeFiltering ? "Show all jurisdictions" : "Show only my jurisdictions");
        this.listView.render();
    }

    protected setupSubViews() {
        this.setupListView();
        this.setupControlsView();
    }

   protected setupListView() {
        const options: any = {itemViewType: this.model.ItemViewType, collection: this.model.RelationCollection};

        if (this.model.ChildTemplate) {
            options.template = this.model.ChildTemplate;
        }
        this.listView = new GenericListView(options);
        this.listRegion.show(this.listView);
    }

    protected setupControlsView() {
        this.controlsView = new ListControlsView(this.model.RelationCollection, (c, f) => this.filter(c, f), {model: this.model});
        this.controlsRegion.show(this.controlsView);
    }

    protected filter(collection: SortableCollection<BaseRelationModel>, text: string): void {
        const filterArray = text.split(" ");
        collection.each((e: BaseRelationModel) => {
            e.FilteredOut = false;
            filterArray.forEach((f) => {
                if (e.FilterableText.toLowerCase().indexOf(f) === -1) {
                    e.FilteredOut = true;
                }
            });
        });
        this.listView.render();
    }

    private showAllRelationsChanged(model, prop): void {
        this.model.RelationCollection.each((item: BaseRelationModel) => {
            item.ForceShow = prop;
        });
        this.model.RelationCollection.setHeadings();
        this.setJurisdictionResultsMessage();
    }

    private showAllJurisdictionsClicked(e: JQueryEventObject): void {
        this.model.State.ShowAllRelations = !this.model.State.ShowAllRelations;
    }

    private collapseHeadingClick(e) {
        const $heading = $(e.currentTarget);
        if (this.$(".dropdown").hasClass("open")) { return; }
        const property = $heading.attr("data-collapse");
        const value = $heading.attr("data-value");
        const collection = this.model.RelationCollection;
        const matches = new Array<Backbone.Model>();

        collection.each((doc: BaseRelationModel) => {
            if ((doc.get(property) === value) && (doc.IsActuallyVisible)) {
                matches.push(doc);
            }
        });

        const collapse = !$(e.currentTarget).parent().hasClass("semi-collapsed");

        _.each(matches, (match) => {
            match.set("SemiCollapsed", collapse);
        });

        this.listView.render();

    }

    private mobileRequestAllRelationsClick(e: JQueryEventObject) {
        this.model.Data.mobileMinimalLoading = false;

        const section = $(e.target).data("section"); // e.e cases-considered or subsequent-cases
        this.model.Data.refresh().then(() => {
            const topOfSection = $("." + section).position().top;
            const parentPos = $("." + section).parent().position().top;
            $(".document-face").scrollTop(-(topOfSection + parentPos)); // Make sure we keep the same section (cited/citing) in view after loading
        });
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/tabs/generic-list-tab.ts